import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  User,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  onAuthStateChanged,
  Auth,
} from 'firebase/auth';
import { auth } from '@/lib/firebase';
import { isAdmin } from '@/lib/admin';

interface AuthContextType {
  user: User | null;
  isAdmin: boolean;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isAdminUser, setIsAdminUser] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!auth) {
      setLoading(false);
      return;
    }

    const unsubscribe = onAuthStateChanged(auth as Auth, async (user) => {
      setUser(user);
      if (user) {
        // Force token refresh to get latest custom claims
        await user.getIdToken(true);
        const adminStatus = await isAdmin(user);
        setIsAdminUser(adminStatus);
      } else {
        setIsAdminUser(false);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const login = async (email: string, password: string) => {
    if (!auth) throw new Error('Auth not initialized');
    setLoading(true);
    try {
      const userCredential = await signInWithEmailAndPassword(auth as Auth, email, password);
      // Force token refresh to get updated custom claims
      await userCredential.user.getIdToken(true);
      // Set user state immediately
      setUser(userCredential.user);
      // Check admin status
      const adminStatus = await isAdmin(userCredential.user);
      setIsAdminUser(adminStatus);
    } finally {
      setLoading(false);
    }
  };

  const loginWithGoogle = async () => {
    if (!auth) throw new Error('Auth not initialized');
    setLoading(true);
    try {
      const provider = new GoogleAuthProvider();
      const userCredential = await signInWithPopup(auth as Auth, provider);
      // Force token refresh to get updated custom claims
      await userCredential.user.getIdToken(true);
      // Set user state immediately
      setUser(userCredential.user);
      // Check admin status
      const adminStatus = await isAdmin(userCredential.user);
      setIsAdminUser(adminStatus);
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    if (!auth) throw new Error('Auth not initialized');
    await signOut(auth as Auth);
    setIsAdminUser(false);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAdmin: isAdminUser,
        loading,
        login,
        loginWithGoogle,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

